import React from 'react';
import styled from 'styled-components';
import { ContactDto, UpdateContactDto, CreateContactDto } from '../services/leadsService';
import { validatePhone, validateEmail } from '../services/leadsService';

// Props interfaces
export interface ContactEntry {
  id: number; // 0 for new entries, positive for existing
  number: string;
  email: string;
  label: string;
  isPrimary: boolean;
  sortOrder: number;
  phoneError?: string;
  emailError?: string;
}

interface ContactsEditorProps {
  contacts: ContactEntry[];
  onChange: (contacts: ContactEntry[]) => void;
  disabled?: boolean;
  maxContacts?: number;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const ContactRowTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const PhoneInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  min-width: 140px;
  max-width: 180px;
  padding: 0.5rem;
  border: 1px solid ${props => props.$hasError ? '#dc2626' : '#dee2e6'};
  border-radius: 4px;
  font-size: 0.875rem;
  direction: ltr;
  text-align: left;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc2626' : '#008EA2'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(220, 38, 38, 0.15)' : 'rgba(0, 142, 162, 0.15)'};
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
`;

const EmailInput = styled.input<{ $hasError?: boolean }>`
  flex: 1;
  min-width: 180px;
  padding: 0.5rem;
  border: 1px solid ${props => props.$hasError ? '#dc2626' : '#dee2e6'};
  border-radius: 4px;
  font-size: 0.875rem;
  direction: ltr;
  text-align: left;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc2626' : '#008EA2'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(220, 38, 38, 0.15)' : 'rgba(0, 142, 162, 0.15)'};
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
`;

const LabelInput = styled.input`
  width: 100px;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #008EA2;
    box-shadow: 0 0 0 2px rgba(0, 142, 162, 0.15);
  }

  &:disabled {
    background-color: #f1f3f5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button<{ $isPrimary: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid ${props => props.$isPrimary ? '#008EA2' : '#dee2e6'};
  border-radius: 4px;
  background: ${props => props.$isPrimary ? '#e7f3ff' : '#fff'};
  color: ${props => props.$isPrimary ? '#008EA2' : '#6c757d'};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: ${props => props.$isPrimary ? '#d0e8ff' : '#f1f3f5'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #fee2e2;
  border-radius: 4px;
  background: #fff;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 1.25rem;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px dashed #008EA2;
  border-radius: 4px;
  background: transparent;
  color: #008EA2;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: #e7f3ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #adb5bd;
    color: #adb5bd;
  }
`;

const ErrorText = styled.span`
  display: block;
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Tooltip = styled.span`
  font-size: 0.7rem;
  color: #6c757d;
`;

// Helper functions
export const convertToContactEntries = (contacts?: ContactDto[]): ContactEntry[] => {
  if (!contacts || contacts.length === 0) {
    return [{
      id: 0,
      number: '',
      email: '',
      label: 'ראשי',
      isPrimary: true,
      sortOrder: 0
    }];
  }

  return contacts.map(c => ({
    id: c.id,
    number: c.number,
    email: c.email || '',
    label: c.label || '',
    isPrimary: c.isPrimary,
    sortOrder: c.sortOrder
  }));
};

export const convertToCreateContactDtos = (entries: ContactEntry[]): CreateContactDto[] => {
  return entries
    .filter(e => e.number.trim() !== '')
    .map((e, index) => ({
      number: e.number.trim(),
      email: e.email.trim() || undefined,
      label: e.label.trim() || undefined,
      isPrimary: e.isPrimary,
      sortOrder: e.sortOrder > 0 ? e.sortOrder : index
    }));
};

const getPrimaryContactEmail = (entries: ContactEntry[]): string =>
  entries.find(e => e.isPrimary)?.email.trim() ?? '';

/**
 * סנכרון שדה הדוא"ל של הלקוח/הליד עם המייל של איש הקשר הראשי (כמו שהטלפון הראשי נגזר ממנו):
 * כשהמייל של הראשי משתנה — בעריכה או בהעברת הכוכב לאיש קשר אחר — ואינו ריק, הוא נכנס לשדה הדוא"ל.
 * אחרת השדה נשאר כפי שהוא, כך שעריכה ידנית של שדה הדוא"ל אחר כך נשמרת.
 */
export const syncEmailWithPrimaryContact = (
  prevEntries: ContactEntry[],
  nextEntries: ContactEntry[],
  currentEmail: string
): string => {
  const nextPrimaryEmail = getPrimaryContactEmail(nextEntries);
  if (nextPrimaryEmail && nextPrimaryEmail !== getPrimaryContactEmail(prevEntries)) {
    return nextPrimaryEmail;
  }
  return currentEmail;
};

export const convertToUpdateContactDtos = (entries: ContactEntry[]): UpdateContactDto[] => {
  return entries
    .filter(e => e.number.trim() !== '')
    .map((e, index) => ({
      id: e.id,
      number: e.number.trim(),
      email: e.email.trim() || undefined,
      label: e.label.trim() || undefined,
      isPrimary: e.isPrimary,
      sortOrder: e.sortOrder > 0 ? e.sortOrder : index
    }));
};

// Main Component
const ContactsEditor: React.FC<ContactsEditorProps> = ({
  contacts,
  onChange,
  disabled = false,
  maxContacts = 10
}) => {
  const handleNumberChange = (index: number, value: string) => {
    const updated = [...contacts];
    updated[index] = {
      ...updated[index],
      number: value,
      phoneError: undefined
    };
    onChange(updated);
  };

  const handleEmailChange = (index: number, value: string) => {
    const updated = [...contacts];
    updated[index] = {
      ...updated[index],
      email: value,
      emailError: undefined
    };
    onChange(updated);
  };

  const handleLabelChange = (index: number, value: string) => {
    const updated = [...contacts];
    updated[index] = {
      ...updated[index],
      label: value
    };
    onChange(updated);
  };

  const handlePrimaryChange = (index: number) => {
    const updated = contacts.map((p, i) => ({
      ...p,
      isPrimary: i === index
    }));
    onChange(updated);
  };

  const handleRemove = (index: number) => {
    if (contacts.length <= 1) return; // השאר לפחות איש קשר אחד

    const updated = contacts.filter((_, i) => i !== index);

    // אובייקט חדש — לא לשנות את הרשומה שמשותפת עם ה-state הקודם
    if (contacts[index].isPrimary && updated.length > 0) {
      updated[0] = { ...updated[0], isPrimary: true };
    }

    onChange(updated);
  };

  const handleAdd = () => {
    if (contacts.length >= maxContacts) return;

    const newEntry: ContactEntry = {
      id: 0,
      number: '',
      email: '',
      label: '',
      isPrimary: false,
      sortOrder: contacts.length
    };
    onChange([...contacts, newEntry]);
  };

  const handlePhoneBlur = (index: number) => {
    const c = contacts[index];
    if (c.number.trim() && !validatePhone(c.number)) {
      const updated = [...contacts];
      updated[index] = { ...updated[index], phoneError: 'מספר טלפון לא תקין' };
      onChange(updated);
    }
  };

  const handleEmailBlur = (index: number) => {
    const c = contacts[index];
    if (c.email.trim() && !validateEmail(c.email)) {
      const updated = [...contacts];
      updated[index] = { ...updated[index], emailError: 'כתובת דוא"ל לא תקינה' };
      onChange(updated);
    }
  };

  return (
    <Container>
      {contacts.map((contact, index) => (
        <ContactRow key={`contact-${index}`}>
          <ContactRowTop>
            <InputColumn>
              <PhoneInput
                type="tel"
                value={contact.number}
                onChange={(e) => handleNumberChange(index, e.target.value)}
                onBlur={() => handlePhoneBlur(index)}
                placeholder="מספר טלפון"
                disabled={disabled}
                $hasError={!!contact.phoneError}
                dir="ltr"
              />
              {contact.phoneError && <ErrorText>{contact.phoneError}</ErrorText>}
            </InputColumn>

            <InputColumn>
              <EmailInput
                type="email"
                value={contact.email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                onBlur={() => handleEmailBlur(index)}
                placeholder="דוא״ל (אופציונלי)"
                disabled={disabled}
                $hasError={!!contact.emailError}
                dir="ltr"
              />
              {contact.emailError && <ErrorText>{contact.emailError}</ErrorText>}
            </InputColumn>

            <LabelInput
              type="text"
              value={contact.label}
              onChange={(e) => handleLabelChange(index, e.target.value)}
              placeholder="תווית"
              disabled={disabled}
            />

            <PrimaryButton
              type="button"
              $isPrimary={contact.isPrimary}
              onClick={() => handlePrimaryChange(index)}
              disabled={disabled || contact.isPrimary}
              title={contact.isPrimary ? 'איש קשר ראשי' : 'הגדר כאיש קשר ראשי'}
            >
              {contact.isPrimary ? '★' : '☆'}
            </PrimaryButton>

            <RemoveButton
              type="button"
              onClick={() => handleRemove(index)}
              disabled={disabled || contacts.length <= 1}
              title="הסר איש קשר"
            >
              ×
            </RemoveButton>
          </ContactRowTop>
        </ContactRow>
      ))}

      <AddButton
        type="button"
        onClick={handleAdd}
        disabled={disabled || contacts.length >= maxContacts}
      >
        + הוסף איש קשר
        {contacts.length >= maxContacts && (
          <Tooltip>(מקסימום {maxContacts})</Tooltip>
        )}
      </AddButton>
    </Container>
  );
};

export default ContactsEditor;
